import { ShipmentContainerMediaInfoDto } from "@/src/store/apis/shipments/shipmentDetail/interface";
import { ContainerMediaType, PhotoTypeMediaInfo, SimpleContainerMediaDto } from "@/src/store/apis/tasks/taskDetail/interface";
import { DEFAULT_PHOTO_TYPE_TITLES, PhotoTypeCode } from "@/src/store/apis/photoType/interface";
import { TaskPhotoTypeRequestResponseDto } from "@/src/store/apis/tasks/taskRegister/interface";
import { TFunction } from "i18next";
import { ReactNode } from "react";

export type ContainerPhotoListType = {
  isActive: boolean;
  maxCount: number;
  uploadedCount: number;
  imageList: ShipmentContainerMediaInfoDto[];
  containerType: ContainerMediaType | PhotoTypeCode;
  title: ReactNode;
  subTitle?: string;
  extraMediaRemark?: string | null;
  displayOrder?: number;
};

export const FIXED_PHOTO_TYPE_TITLES: Record<string, string> = {
  CLOSE_UP_SEAL: "Close-Up Seal",
  EXTRA: "Extra",
};

/**
 * 커스텀 사진 타입 코드 목록 (CUSTOM_1 ~ CUSTOM_8)
 */
export const CUSTOM_PHOTO_TYPE_CODES = [
  "CUSTOM_1",
  "CUSTOM_2",
  "CUSTOM_3",
  "CUSTOM_4",
  "CUSTOM_5",
  "CUSTOM_6",
  "CUSTOM_7",
  "CUSTOM_8",
] as const;

// isCustomPhotoType moved to @/src/utils/photoTypeTitle.ts

export const camelCaseToPhotoTypeCode = (key: string): PhotoTypeCode | null => {
  const standardMap: Record<string, PhotoTypeCode> = {
    sampleBale: "SAMPLE_BALE",
    empty: "EMPTY",
    quarterLoaded: "QUARTER_LOADED",
    halfLoaded: "HALF_LOADED",
    fullLoaded: "FULL_LOADED",
    oneDoorClosed: "ONE_DOOR_CLOSED",
    bothDoorsClosed: "BOTH_DOORS_CLOSED",
    closeUpSeal: "CLOSE_UP_SEAL",
    extra: "EXTRA",
  };

  if (standardMap[key]) {
    return standardMap[key];
  }

  const customMatch = key.match(/^custom(\d+)$/);
  if (customMatch) {
    return `CUSTOM_${customMatch[1]}` as PhotoTypeCode;
  }

  return null;
};

/**
 * photoTypeRequests에서 특정 타입의 정보를 조회
 */
export const getPhotoTypeRequestInfo = (
  photoTypeRequests: TaskPhotoTypeRequestResponseDto[] | undefined,
  typeCode: PhotoTypeCode | ContainerMediaType
): TaskPhotoTypeRequestResponseDto | undefined => {
  return photoTypeRequests?.find((req) => req.typeCode === typeCode);
};

/**
 * 표준 ContainerMediaType을 PhotoTypeCode로 변환
 */
export const containerMediaTypeToPhotoTypeCode = (
  containerMediaType: ContainerMediaType
): PhotoTypeCode => {
  const mapping: Record<ContainerMediaType, PhotoTypeCode> = {
    SAMPLE_BALE: "SAMPLE_BALE",
    EMPTY: "EMPTY",
    QUARTER_LOADED: "QUARTER_LOADED",
    HALF_LOADED: "HALF_LOADED",
    FULL_LOADED: "FULL_LOADED",
    ONE_DOOR_CLOSED: "ONE_DOOR_CLOSED",
    BOTH_DOORS_CLOSED: "BOTH_DOORS_CLOSED",
    CLOSE_UP_SEAL: "CLOSE_UP_SEAL",
    EXTRA: "EXTRA",
  };
  return mapping[containerMediaType];
};

export const photoTypeMediaInfoToContainerPhoto = (
  typeCode: string,
  mediaInfo: PhotoTypeMediaInfo,
  photoTypeRequests?: TaskPhotoTypeRequestResponseDto[]
): ContainerPhotoListType => {
  const photoTypeRequest = photoTypeRequests?.find(
    (req) => req.typeCode === typeCode
  );

  const imageList: ShipmentContainerMediaInfoDto[] = (mediaInfo.medias || []).map(
    (media: SimpleContainerMediaDto) => ({
      createdAt: media.containerMediaCreatedAt || "",
      mediaUrl: media.containerPhotoSimpleMedia?.mediaUrl || "",
      sSizeImageUrl: media.containerPhotoSimpleMedia?.sSizeImageUrl,
      containerMediaType: (media.typeCode || typeCode) as ContainerMediaType,
      containerMediaId: media.containerMediaId,
      coordinate: media.coordinate,
    })
  );

  const title =
    photoTypeRequest?.title || FIXED_PHOTO_TYPE_TITLES[typeCode] || typeCode;

  return {
    isActive: mediaInfo.requiredCount > 0,
    maxCount: mediaInfo.requiredCount,
    uploadedCount: mediaInfo.uploadedCount,
    imageList,
    containerType: typeCode as PhotoTypeCode,
    title,
    subTitle: photoTypeRequest?.subTitle,
    displayOrder: mediaInfo.displayOrder ?? photoTypeRequest?.displayOrder,
    extraMediaRemark: mediaInfo.remark ?? undefined,
  };
};

export const MEDIA_COUNT_KEY_LIST = [
  "containerSampleBaleMediasCount",
  "containerEmptyMediasCount",
  "containerQuarterLoadedMediasCount",
  "containerHalfLoadedMediasCount",
  "containerFullLoadedMediasCount",
  "containerOneDoorClosedMediasCount",
  "containerBothDoorsClosedMediasCount",
  "containerCloseUpSealMediasCount",
  "containerExtraMediasCount",
] as const;

export const PHOTO_TYPE_LIST = [
  "sampleBale",
  "empty",
  "quarterLoaded",
  "halfLoaded",
  "fullLoaded",
  "oneDoorClosed",
  "bothDoorsClosed",
  "closeUpSeal",
  "extra",
] as const;

export const MEDIA_UPLOAD_COUNT_MAP: Record<
  (typeof MEDIA_COUNT_KEY_LIST)[number],
  string
> = {
  containerSampleBaleMediasCount: "uploadedContainerSampleBaleMediasCount",
  containerEmptyMediasCount: "uploadedContainerEmptyMediasCount",
  containerQuarterLoadedMediasCount:
    "uploadedContainerQuarterLoadedMediasCount",
  containerHalfLoadedMediasCount: "uploadedContainerHalfLoadedMediasCount",
  containerFullLoadedMediasCount: "uploadedContainerFullLoadedMediasCount",
  containerOneDoorClosedMediasCount:
    "uploadedContainerOneDoorClosedMediasCount",
  containerBothDoorsClosedMediasCount:
    "uploadedContainerBothDoorsClosedMediasCount",
  containerCloseUpSealMediasCount: "uploadedContainerCloseUpSealMediasCount",
  containerExtraMediasCount: "uploadedContainerExtraMediasCount",
} as const;

export const MEDIA_MAP: Record<(typeof MEDIA_COUNT_KEY_LIST)[number], string> =
  {
    containerSampleBaleMediasCount: "sampleBaleMediaInfoList",
    containerEmptyMediasCount: "emptyMediaInfoList",
    containerQuarterLoadedMediasCount: "quarterLoadedMediaInfoList",
    containerHalfLoadedMediasCount: "halfLoadedMediaInfoList",
    containerFullLoadedMediasCount: "fullLoadedMediaInfoList",
    containerOneDoorClosedMediasCount: "oneDoorClosedMediaInfoList",
    containerBothDoorsClosedMediasCount: "bothDoorsClosedMediaInfoList",
    containerCloseUpSealMediasCount: "closeUpSealMediaInfoList",
    containerExtraMediasCount: "extraMediaInfoList",
  };

export const MEDIA_CONTAINER_MAP: Record<
  (typeof MEDIA_COUNT_KEY_LIST)[number],
  ContainerMediaType
> = {
  containerSampleBaleMediasCount: "SAMPLE_BALE",
  containerEmptyMediasCount: "EMPTY",
  containerQuarterLoadedMediasCount: "QUARTER_LOADED",
  containerHalfLoadedMediasCount: "HALF_LOADED",
  containerFullLoadedMediasCount: "FULL_LOADED",
  containerOneDoorClosedMediasCount: "ONE_DOOR_CLOSED",
  containerBothDoorsClosedMediasCount: "BOTH_DOORS_CLOSED",
  containerCloseUpSealMediasCount: "CLOSE_UP_SEAL",
  containerExtraMediasCount: "EXTRA",
};

export const PHOTO_TYPE_CONTAINER_VALUE_TYPE_MAP: Record<
  (typeof PHOTO_TYPE_LIST)[number],
  ContainerMediaType
> = {
  sampleBale: "SAMPLE_BALE",
  empty: "EMPTY",
  quarterLoaded: "QUARTER_LOADED",
  halfLoaded: "HALF_LOADED",
  fullLoaded: "FULL_LOADED",
  oneDoorClosed: "ONE_DOOR_CLOSED",
  bothDoorsClosed: "BOTH_DOORS_CLOSED",
  closeUpSeal: "CLOSE_UP_SEAL",
  extra: "EXTRA",
};

export const PHOTO_TYPE_CONTAINER_LABEL_TYPE_MAP = (
  t: TFunction,
): Record<(typeof PHOTO_TYPE_LIST)[number], string> => ({
  sampleBale: t("task:add.photoType.sampleBale"),
  empty: t("task:add.photoType.empty"),
  quarterLoaded: t("task:add.photoType.quarterLoaded"),
  halfLoaded: t("task:add.photoType.halfLoaded"),
  fullLoaded: t("task:add.photoType.fullLoaded"),
  oneDoorClosed: t("task:add.photoType.oneDoorClosed"),
  bothDoorsClosed: t("task:add.photoType.bothDoorsClosed"),
  closeUpSeal: FIXED_PHOTO_TYPE_TITLES.CLOSE_UP_SEAL,
  extra: FIXED_PHOTO_TYPE_TITLES.EXTRA,
});

export const CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP = (
  t: TFunction,
): Record<ContainerMediaType, string> => ({
  SAMPLE_BALE: t("task:add.photoType.sampleBale"),
  EMPTY: t("task:add.photoType.empty"),
  QUARTER_LOADED: t("task:add.photoType.quarterLoaded"),
  HALF_LOADED: t("task:add.photoType.halfLoaded"),
  FULL_LOADED: t("task:add.photoType.fullLoaded"),
  ONE_DOOR_CLOSED: t("task:add.photoType.oneDoorClosed"),
  BOTH_DOORS_CLOSED: t("task:add.photoType.bothDoorsClosed"),
  CLOSE_UP_SEAL: FIXED_PHOTO_TYPE_TITLES.CLOSE_UP_SEAL,
  EXTRA: FIXED_PHOTO_TYPE_TITLES.EXTRA,
});

// getPhotoTypeTitleForDownload moved to @/src/utils/photoTypeTitle.ts
